import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "../../configurator-store";
import { MultiSelectChip } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LabelsMultiSelectChipRef } from "./labels-multi-select-chip-types";

const mapStateToProps = (state: RootState) => ({
  labels: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels
});

const ownActions = {
  postLabelsRequest: configuratorActions.postLabelsRequest
};

type Props = typeof ownActions &
  ReturnType<typeof mapStateToProps> & {
    projectId: string;
    entityIds: string[];
    onChange?: (values: string[]) => void;
    onLoadingChange?: (isLoading: boolean) => void;
    onSaved?: () => void;
    applyOnBlur?: boolean;
    darkMode?: boolean;
    floatingLabelBg?: string;
  };

const LabelsMultiSelectChipBase = forwardRef<LabelsMultiSelectChipRef, Props>((props, ref) => {
  const { labels, postLabelsRequest, entityIds, projectId, onChange, onLoadingChange, onSaved, applyOnBlur = false, darkMode, floatingLabelBg } = props;

  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  // Tracks which entityIds were last used to sync from Redux. Prevents another
  // chip instance's save (which updates shared `labels` state) from overwriting
  // unsaved selections in this chip — only re-sync when the user switches entity.
  const lastSyncedEntityIdsRef = useRef<string | null>(null);
  // Last committed selection (from Redux sync or a successful apply) — what dirty-checking compares against
  const committedLabelsRef = useRef<string[]>([]);

  const configuratorPageT = useConfiguratorPageT();

  useEffect(() => {
    if (!labels) return;

    const entityIdsKey = JSON.stringify(entityIds);
    // Skip if entityIds haven't changed — labels updated because another chip saved,
    // not because the user navigated to a different entity.
    if (lastSyncedEntityIdsRef.current === entityIdsKey) return;

    lastSyncedEntityIdsRef.current = entityIdsKey;
    const temp = new Set<string>();
    labels.forEach(label => {
      if (label.references.findIndex(reference => reference.entityId && entityIds.includes(reference.entityId)) >= 0) {
        temp.add(label.shortname);
      }
    });
    setSelectedLabels(Array.from(temp));
    committedLabelsRef.current = Array.from(temp);
  }, [labels, JSON.stringify(entityIds)]);

  const handleApply = (onSuccess?: () => void) => {
    if (!projectId || !labels) return;

    onLoadingChange?.(true);
    postLabelsRequest(projectId, entityIds, selectedLabels, isSuccess => {
      onLoadingChange?.(false);
      if (isSuccess) {
        committedLabelsRef.current = selectedLabels;
        onSuccess?.();
      }
    });
  };

  // Autosave trigger: only when the selection actually changed since the last commit
  const handleBlur = () => {
    const current = [...selectedLabels].sort();
    const committed = [...committedLabelsRef.current].sort();
    if (JSON.stringify(current) === JSON.stringify(committed)) return;
    handleApply(onSaved);
  };

  useImperativeHandle(ref, () => ({ handleApply }), [handleApply]);

  return (
    <MultiSelectChip
      label={configuratorPageT.label}
      value={selectedLabels.sort((a, b) => a.localeCompare(b))}
      onChange={(values: string[]) => {
        setSelectedLabels(values);
        onChange?.(values);
      }}
      onBlur={applyOnBlur ? handleBlur : undefined}
      options={labels ? labels.map(label => label.shortname).sort((a, b) => a.localeCompare(b)) : []}
      darkMode={darkMode}
      floatingLabelBg={floatingLabelBg}
    />
  );
});

LabelsMultiSelectChipBase.displayName = "LabelsMultiSelectChip";

export const LabelsMultiSelectChip = connect(mapStateToProps, ownActions, undefined, { forwardRef: true })(LabelsMultiSelectChipBase);
